import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  MapPin, DollarSign, Trash2, Edit,
  Sparkles, ChevronDown, ChevronUp, Loader2,
  FileText
} from "lucide-react";
import { STATUS_CONFIG, DEAL_LABELS, fmt, fmtEur, MarkdownContent } from "./investmentUtils";

export const InvestmentCard = ({ inv, onEdit, onDelete, onAnalyze, onGenerateOffer, analyzing, generatingOffer }) => {
  const [expanded, setExpanded] = useState(false);
  const f = inv.financials || {};
  const status = STATUS_CONFIG[inv.status] || STATUS_CONFIG.new;
  const profitable = f.profit >= 0;
  const hasCommercial = f.comm_net_m2 > 0;

  return (
    <Card data-testid={`investment-card-${inv.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-semibold text-slate-800 truncate">{inv.name}</h3>
              <Badge className={status.color}>{status.label}</Badge>
              <Badge variant="outline" className="text-xs">{DEAL_LABELS[inv.deal_type]}</Badge>
            </div>
            {inv.location && <p className="text-sm text-slate-500 flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" />{inv.location}</p>}
          </div>
          <div className="flex gap-1 ml-2">
            <Button variant="outline" size="sm" onClick={() => onGenerateOffer(inv.id)} disabled={generatingOffer === inv.id} data-testid={`offer-btn-${inv.id}`} className="text-xs text-blue-600 border-blue-200 hover:bg-blue-50">
              {generatingOffer === inv.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1" />}Offer
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onAnalyze(inv.id)} disabled={analyzing === inv.id} data-testid={`analyze-btn-${inv.id}`} title="AI Analysis">
              {analyzing === inv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-violet-500" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onEdit(inv)} data-testid={`edit-btn-${inv.id}`}><Edit className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" onClick={() => onDelete(inv.id)} data-testid={`delete-btn-${inv.id}`}><Trash2 className="w-4 h-4 text-red-400" /></Button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          <div className="p-2.5 rounded-sm bg-slate-50 text-center">
            <span className="text-[11px] text-slate-400 block">Total Net</span>
            <span className="text-sm font-bold text-slate-700">{fmt(f.total_net_m2)} m²</span>
          </div>
          <div className="p-2.5 rounded-sm bg-slate-50 text-center">
            <span className="text-[11px] text-slate-400 block">Your Area</span>
            <span className="text-sm font-bold text-emerald-700">{fmt(f.your_total_m2)} m²</span>
          </div>
          <div className="p-2.5 rounded-sm bg-slate-50 text-center">
            <span className="text-[11px] text-slate-400 block">Total Cost</span>
            <span className="text-sm font-bold text-slate-700">{fmtEur(f.total_costs)}</span>
          </div>
          <div className={`p-2.5 rounded-sm text-center ${profitable ? "bg-emerald-50" : "bg-red-50"}`}>
            <span className={`text-[11px] block ${profitable ? "text-emerald-500" : "text-red-400"}`}>Profit</span>
            <span className={`text-sm font-bold ${profitable ? "text-emerald-700" : "text-red-600"}`}>{fmtEur(f.profit)}</span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 text-xs text-slate-400 flex-wrap">
            <span>Revenue: {fmtEur(f.total_revenue)}</span>
            <span>Margin: <span className={profitable ? "text-emerald-600" : "text-red-500"}>{f.profit_margin?.toFixed(1)}%</span></span>
            {inv.deal_type !== "cash" && <span>Barter value: {fmtEur(f.barter_total_value)}</span>}
            {hasCommercial && <span>Commercial: {fmt(f.comm_net_m2)} m²</span>}
          </div>
          <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)} className="text-xs" data-testid={`expand-btn-${inv.id}`}>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {expanded ? "Less" : "Details"}
          </Button>
        </div>

        {expanded && (
          <div className="mt-4 pt-4 border-t space-y-4">
            {inv.description && <p className="text-sm text-slate-600">{inv.description}</p>}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <div><span className="text-slate-400 text-xs block">Land Area</span>{fmt(inv.total_land_area_m2)} m²</div>
              <div><span className="text-slate-400 text-xs block">Residential Gross</span>{fmt(f.res_gross_m2)} m²</div>
              <div><span className="text-slate-400 text-xs block">Residential Net</span>{fmt(f.res_net_m2)} m² ({inv.deduction_percentage}% ded.)</div>
              {hasCommercial && <>
                <div><span className="text-slate-400 text-xs block">Commercial Gross</span>{fmt(inv.commercial_gross_m2)} m²</div>
                <div><span className="text-slate-400 text-xs block">Commercial Net</span>{fmt(f.comm_net_m2)} m² ({inv.commercial_deduction_percentage || 0}% ded.)</div>
              </>}
              {inv.deal_type !== "barter" && <div><span className="text-slate-400 text-xs block">Land Price</span>{fmtEur(inv.land_price)}</div>}
              {inv.deal_type === "mixed" && <div><span className="text-slate-400 text-xs block">Cash Portion</span>{fmtEur(inv.cash_portion)}</div>}
              {inv.deal_type !== "cash" && <>
                <div><span className="text-slate-400 text-xs block">Owner %</span>{inv.owner_percentage}%</div>
                <div><span className="text-slate-400 text-xs block">Owner Residential</span>{fmt(f.owner_res_m2)} m²</div>
                {hasCommercial && <div><span className="text-slate-400 text-xs block">Owner Commercial</span>{fmt(f.owner_comm_m2)} m²</div>}
                <div><span className="text-slate-400 text-xs block">Barter Cash Value</span>{fmtEur(f.barter_total_value)}</div>
              </>}
              <div><span className="text-slate-400 text-xs block">Communal Tax/m²</span>{fmtEur(inv.communal_tax_per_m2)}</div>
              <div><span className="text-slate-400 text-xs block">Construction/m²</span>{fmtEur(inv.construction_cost_per_m2)}</div>
              <div><span className="text-slate-400 text-xs block">Residential Price/m²</span>{fmtEur(inv.residential_sale_price_per_m2 || inv.sale_price_per_m2)}</div>
              {hasCommercial && <div><span className="text-slate-400 text-xs block">Commercial Price/m²</span>{fmtEur(inv.commercial_sale_price_per_m2)}</div>}
              <div><span className="text-slate-400 text-xs block">Cost per m² Built</span>{fmtEur(f.cost_per_m2_built)}</div>
            </div>
            {inv.notes && <div className="p-3 rounded-sm bg-slate-50 text-sm text-slate-600"><span className="font-medium">Notes:</span> {inv.notes}</div>}

            {inv.ai_analysis && (
              <div className="p-4 rounded-sm bg-violet-50/70 border border-violet-100">
                <p className="text-xs font-semibold text-violet-600 flex items-center gap-1 mb-3"><Sparkles className="w-3 h-3" />AI Analysis</p>
                <div data-testid={`ai-analysis-${inv.id}`}><MarkdownContent text={inv.ai_analysis} /></div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
